/**
 * precision.ts — Section 4: "Precision, Recall, F1 — What Kind of Wrong?".
 *
 * Same two-histogram view as Section 3, but now each sample is coloured by its
 * confusion-matrix role:
 *   - TP (true positive):  positive AND predicted positive  — emerald
 *   - FP (false positive): negative AND predicted positive  — rose
 *   - FN (false negative): positive AND predicted negative  — amber
 *   - TN (true negative):  negative AND predicted negative  — slate (faded)
 *
 * The threshold slider still drives everything; the readouts switch to
 * Precision, Recall, and F1. The status line names the trade-off explicitly:
 * you cannot raise precision without lowering recall (and vice versa) unless
 * the model itself gets better.
 */

import { classificationScores, confusion, safeDiv, type ClassSample } from './data';

export function initPrecision(): void {
  const canvasEl = document.getElementById('precisionCanvas') as HTMLCanvasElement | null;
  const sliderEl = document.getElementById('precisionThresholdSlider') as HTMLInputElement | null;
  const thrValueEl = document.getElementById('precisionThresholdValue');
  const precValue = document.getElementById('precisionValue');
  const recValue = document.getElementById('recallValue');
  const f1Value = document.getElementById('f1Value');
  const statusEl = document.getElementById('precisionStatus');
  if (
    !canvasEl || !sliderEl || !thrValueEl || !precValue || !recValue || !f1Value || !statusEl
  ) return;

  const canvas = canvasEl;
  const slider = sliderEl;
  const thrValue = thrValueEl;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const W = canvas.width;
  const H = canvas.height;
  const PAD = 40;

  const mapX = (s: number): number => PAD + s * (W - 2 * PAD);

  const samples: ClassSample[] = classificationScores(0);
  // Sort per-class scores so we can draw each sample as a tall tick along the
  // x-axis at its score value — a rug plot. Stacked by class to avoid overlap.
  const negatives = samples.filter((s) => s.label === 0).map((s) => s.score).sort((a, b) => a - b);
  const positives = samples.filter((s) => s.label === 1).map((s) => s.score).sort((a, b) => a - b);

  function drawAxes(): void {
    ctx!.strokeStyle = '#e2e8f0';
    ctx!.lineWidth = 1;
    ctx!.beginPath();
    for (let i = 0; i <= 10; i++) {
      const x = mapX(i / 10);
      ctx!.moveTo(x, PAD);
      ctx!.lineTo(x, H - PAD);
    }
    ctx!.stroke();

    // Two baseline guide lines — one for each class's rug.
    ctx!.strokeStyle = '#cbd5e1';
    ctx!.beginPath();
    ctx!.moveTo(PAD, H / 2);
    ctx!.lineTo(W - PAD, H / 2);
    ctx!.moveTo(PAD, H - PAD);
    ctx!.lineTo(W - PAD, H - PAD);
    ctx!.stroke();
  }

  function draw(): void {
    const threshold = parseFloat(slider.value);
    const c = confusion(samples, threshold);

    const precision = safeDiv(c.tp, c.tp + c.fp);
    const recall = safeDiv(c.tp, c.tp + c.fn);
    const f1 = safeDiv(2 * precision * recall, precision + recall);

    ctx!.clearRect(0, 0, W, H);
    drawAxes();

    // Threshold line.
    ctx!.strokeStyle = '#1e293b';
    ctx!.lineWidth = 2;
    ctx!.setLineDash([6, 3]);
    ctx!.beginPath();
    ctx!.moveTo(mapX(threshold), PAD);
    ctx!.lineTo(mapX(threshold), H - PAD);
    ctx!.stroke();
    ctx!.setLineDash([]);

    // Rug plot — each sample is a short vertical tick. Colour by role.
    // Negatives sit on the lower baseline (y = H - PAD), positives on the
    // midline (y = H/2). This separation makes TP/FP/FN visually parseable.
    const tickH = 16;
    const roleForNeg = (score: number): 'tn' | 'fp' => (score >= threshold ? 'fp' : 'tn');
    const roleForPos = (score: number): 'fn' | 'tp' => (score >= threshold ? 'tp' : 'fn');

    // Negatives.
    for (const s of negatives) {
      ctx!.strokeStyle = roleForNeg(s) === 'fp' ? '#c44e52' : '#94a3b8';
      ctx!.lineWidth = roleForNeg(s) === 'fp' ? 2 : 1;
      ctx!.beginPath();
      ctx!.moveTo(mapX(s), H - PAD);
      ctx!.lineTo(mapX(s), H - PAD - tickH);
      ctx!.stroke();
    }
    // Positives (hang downward from the midline so the two rugs don't overlap).
    for (const s of positives) {
      const role = roleForPos(s);
      ctx!.strokeStyle = role === 'tp' ? '#55a868' : role === 'fn' ? '#f59e0b' : '#94a3b8';
      ctx!.lineWidth = role === 'tp' || role === 'fn' ? 2 : 1;
      ctx!.beginPath();
      ctx!.moveTo(mapX(s), H / 2);
      ctx!.lineTo(mapX(s), H / 2 + tickH);
      ctx!.stroke();
    }

    // Readouts.
    thrValue!.textContent = threshold.toFixed(2);
    precValue!.textContent = precision.toFixed(2);
    recValue!.textContent = recall.toFixed(2);
    f1Value!.textContent = f1.toFixed(2);

    // Status: name the trade-off. The four canonical regimes:
    //   - high threshold → high precision, low recall (few FPs, miss positives)
    //   - low threshold  → low precision, high recall (catch all, many FPs)
    if (c.tp + c.fn === 0) {
      statusEl!.textContent = 'No positives in range.';
      statusEl!.className = 'text-sm mt-3 font-semibold text-slate-600';
    } else if (precision > 0.85 && recall > 0.85) {
      statusEl!.textContent = 'Both high — rare and good. The classes separate well at this threshold.';
      statusEl!.className = 'text-sm mt-3 font-semibold text-emerald-600';
    } else if (precision > recall + 0.15) {
      statusEl!.textContent = `Precision ${(precision * 100).toFixed(0)}% but recall only ${(recall * 100).toFixed(0)}% — you're missing positives (FN = ${c.fn}). Lower the threshold to catch more.`;
      statusEl!.className = 'text-sm mt-3 font-semibold text-amber-600';
    } else if (recall > precision + 0.15) {
      statusEl!.textContent = `Recall ${(recall * 100).toFixed(0)}% but precision only ${(precision * 100).toFixed(0)}% — too many false alarms (FP = ${c.fp}). Raise the threshold to be more selective.`;
      statusEl!.className = 'text-sm mt-3 font-semibold text-amber-600';
    } else {
      statusEl!.textContent = `Precision and recall are balanced but modest. F1 = ${f1.toFixed(2)} — moving the threshold trades one for the other.`;
      statusEl!.className = 'text-sm mt-3 font-semibold text-rose-600';
    }
  }

  slider.addEventListener('input', draw);
  draw();
}
